import type { FloorplanRoom } from "./types";

/** Same value as the backend's MERGE_ADJACENCY_TOLERANCE (crm/services/room_merge.py), in floorplan units. */
export const MERGED_EDGE_TOLERANCE = 25;

type Pt = [number, number];
export type OutlineSegment = [Pt, Pt];

/**
 * Fold merged parts (#416) into their main room: the part disappears from the list,
 * its polygons are appended after the main room's own, its m² and furniture are
 * added, and ``mergedPartIds`` is set — which makes ``FloorplanRoomOverlay`` draw the
 * group as one room (one fill, outer outline, one label centred on the whole group).
 * A part whose main room is not in ``rooms`` is left as is.
 */
export function foldMergedRooms(rooms: FloorplanRoom[]): FloorplanRoom[] {
  const ids = new Set(rooms.map((r) => r.id));
  const partsByMain = new Map<number, FloorplanRoom[]>();
  for (const r of rooms) {
    if (r.mergedIntoId != null && ids.has(r.mergedIntoId)) {
      partsByMain.set(r.mergedIntoId, [...(partsByMain.get(r.mergedIntoId) ?? []), r]);
    }
  }
  if (partsByMain.size === 0) return rooms;
  return rooms
    .filter((r) => r.mergedIntoId == null || !ids.has(r.mergedIntoId))
    .map((r) => {
      const parts = partsByMain.get(r.id);
      if (!parts) return r;
      const areas = [r.squareMeters, ...parts.map((p) => p.squareMeters)].filter(
        (v): v is number => typeof v === "number",
      );
      return {
        ...r,
        polygon: [...(r.polygon ?? []), ...parts.flatMap((p) => (p.polygon ?? []).filter((poly) => poly.length > 0))],
        squareMeters: areas.length ? areas.reduce((a, b) => a + b, 0) : r.squareMeters,
        furniture: [...(r.furniture ?? []), ...parts.flatMap((p) => p.furniture ?? [])],
        mergedPartIds: parts.map((p) => p.id),
      };
    });
}

/** Distances up to this (floorplan units) count as touching: such parts need no bridge. */
const TOUCHING = 0.5;
/** How far outside an edge the union-boundary test probes. */
const PROBE = 0.01;

interface EdgeInfo {
  a: number[];
  b: number[];
  dx: number;
  dy: number;
  len: number;
  /** Outward unit normal (away from the edge's own polygon), whatever the ring's winding. */
  nx: number;
  ny: number;
}

function signedArea(poly: number[][]): number {
  let a = 0;
  poly.forEach((p, i) => {
    const q = poly[(i + 1) % poly.length];
    a += p[0] * q[1] - q[0] * p[1];
  });
  return a / 2;
}

function ringEdges(poly: number[][]): EdgeInfo[] {
  const sign = signedArea(poly) >= 0 ? 1 : -1;
  return poly.map((a, i) => {
    const b = poly[(i + 1) % poly.length];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len = Math.hypot(dx, dy);
    // Interior is left of a→b for positive area, so outward is the right-hand normal.
    return { a, b, dx, dy, len, nx: len ? (sign * dy) / len : 0, ny: len ? (-sign * dx) / len : 0 };
  });
}

/** Signed distances of ``c``/``d`` from ``e``'s line along its outward normal, and ``[lo, hi]`` of ``e`` the edge ``c→d`` spans. */
function relate(e: EdgeInfo, f: EdgeInfo) {
  const sc = (f.a[0] - e.a[0]) * e.nx + (f.a[1] - e.a[1]) * e.ny;
  const sd = (f.b[0] - e.a[0]) * e.nx + (f.b[1] - e.a[1]) * e.ny;
  const len2 = e.len * e.len;
  const tc = ((f.a[0] - e.a[0]) * e.dx + (f.a[1] - e.a[1]) * e.dy) / len2;
  const td = ((f.b[0] - e.a[0]) * e.dx + (f.b[1] - e.a[1]) * e.dy) / len2;
  return { sc, sd, lo: Math.max(0, Math.min(tc, td)), hi: Math.min(1, Math.max(tc, td)) };
}

const parallel = (e: EdgeInfo, f: EdgeInfo) => Math.abs(e.dx * f.dy - e.dy * f.dx) / (e.len * f.len) <= 0.02;

/**
 * Every FACING pair: edge ``e`` of polygon ``pi`` and edge ``f`` of another polygon
 * ``oi``, parallel, with opposite outward normals (``f``'s polygon is on the far side
 * of ``e``, looking back at it), ``f`` 0…``tolerance`` outside ``e`` — a shared wall
 * or a wall gap. ``[lo, hi]`` is the stretch of ``e`` that ``f`` spans.
 */
function forEachFacingEdge(
  rings: EdgeInfo[][],
  tolerance: number,
  visit: (m: { pi: number; ei: number; oi: number; e: EdgeInfo; f: EdgeInfo; lo: number; hi: number; dist: number }) => void,
): void {
  rings.forEach((own, pi) =>
    own.forEach((e, ei) => {
      if (e.len === 0) return;
      rings.forEach((other, oi) => {
        if (oi === pi) return;
        for (const f of other) {
          if (f.len === 0 || !parallel(e, f) || e.nx * f.nx + e.ny * f.ny > -0.9) continue;
          const { sc, sd, lo, hi } = relate(e, f);
          if (Math.min(sc, sd) < -TOUCHING || Math.max(sc, sd) > tolerance) continue;
          if (hi > lo) visit({ pi, ei, oi, e, f, lo, hi, dist: (sc + sd) / 2 });
        }
      });
    }),
  );
}

/**
 * Quads that fill the wall gap (≤ ``tolerance``) between facing edges of two parts,
 * so a merged room reads as one floor area. Each quad spans the stretch where the two
 * edges overlap; parts that touch or overlap get none.
 */
export function mergedRoomBridges(polygons: number[][][], tolerance = MERGED_EDGE_TOLERANCE): number[][][] {
  const bridges: number[][][] = [];
  forEachFacingEdge(polygons.map(ringEdges), tolerance, ({ pi, oi, e, f, lo, hi, dist }) => {
    if (pi > oi || dist <= TOUCHING) return; // one quad per facing pair
    const at = (s: number) => [e.a[0] + e.dx * s, e.a[1] + e.dy * s];
    const onF = (p: number[]) => {
      const t = ((p[0] - f.a[0]) * f.dx + (p[1] - f.a[1]) * f.dy) / (f.len * f.len);
      return [f.a[0] + f.dx * t, f.a[1] + f.dy * t];
    };
    const p1 = at(lo);
    const p2 = at(hi);
    bridges.push([p1, p2, onF(p2), onF(p1)]);
  });
  return bridges;
}

function pointInPolygon(x: number, y: number, poly: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Parameters in (0, 1) where ``f`` crosses or touches ``e``. */
function crossings(e: EdgeInfo, f: EdgeInfo): number[] {
  const out: number[] = [];
  const denom = e.dx * f.dy - e.dy * f.dx;
  const wx = f.a[0] - e.a[0];
  const wy = f.a[1] - e.a[1];
  if (Math.abs(denom) > 1e-12) {
    const t = (wx * f.dy - wy * f.dx) / denom;
    const u = (wx * e.dy - wy * e.dx) / denom;
    if (t > 1e-9 && t < 1 - 1e-9 && u >= -1e-9 && u <= 1 + 1e-9) out.push(t);
  } else {
    // Collinear (or parallel): f's endpoints that lie on e split it.
    for (const p of [f.a, f.b]) {
      const dist = Math.abs(e.dx * (p[1] - e.a[1]) - e.dy * (p[0] - e.a[0])) / e.len;
      const t = ((p[0] - e.a[0]) * e.dx + (p[1] - e.a[1]) * e.dy) / (e.len * e.len);
      if (dist <= 1e-6 && t > 1e-9 && t < 1 - 1e-9) out.push(t);
    }
  }
  return out;
}

/**
 * The outer outline of a merged room — the boundary of the union of its polygons
 * and their gap bridges. An edge stretch is dropped when it is (a) a shared wall or
 * a gap ≤ ``tolerance`` to a facing edge of another polygon, (b) inside another
 * polygon (contained or overlapping parts), or (c) a duplicate of a collinear edge
 * of an earlier polygon. What is left is one closed outline.
 */
export function mergedOutlineSegments(
  polygons: number[][][],
  tolerance = MERGED_EDGE_TOLERANCE,
  bridges: number[][][] = mergedRoomBridges(polygons, tolerance),
): OutlineSegment[] {
  const all = [...polygons, ...bridges].filter((poly) => poly.length >= 3);
  const rings = all.map(ringEdges);
  const covered = rings.map((r) => r.map(() => [] as [number, number][]));
  forEachFacingEdge(rings, tolerance, ({ pi, ei, lo, hi }) => covered[pi][ei].push([lo, hi]));
  const out: OutlineSegment[] = [];
  rings.forEach((own, pi) =>
    own.forEach((e, ei) => {
      if (e.len === 0) return;
      const cuts = [0, 1];
      for (const [lo, hi] of covered[pi][ei]) cuts.push(lo, hi);
      rings.forEach((other, oi) => {
        if (oi === pi) return;
        for (const f of other) {
          if (f.len === 0) continue;
          cuts.push(...crossings(e, f));
          // A collinear same-side edge of an EARLIER polygon draws this stretch already.
          if (oi < pi && parallel(e, f) && e.nx * f.nx + e.ny * f.ny > 0.9) {
            const { sc, sd, lo, hi } = relate(e, f);
            if (Math.abs(sc) <= TOUCHING && Math.abs(sd) <= TOUCHING && hi > lo) {
              covered[pi][ei].push([lo, hi]);
              cuts.push(lo, hi);
            }
          }
        }
      });
      const ts = [...new Set(cuts)].sort((x, y) => x - y);
      const at = (s: number): Pt => [e.a[0] + e.dx * s, e.a[1] + e.dy * s];
      let runStart: number | null = null;
      for (let k = 0; k < ts.length - 1; k++) {
        const t0 = ts[k];
        const t1 = ts[k + 1];
        if (t1 - t0 < 1e-9) continue;
        const mid = (t0 + t1) / 2;
        const [mx, my] = at(mid);
        const keep =
          !covered[pi][ei].some(([lo, hi]) => mid > lo && mid < hi) &&
          !all.some((poly, oi) => oi !== pi && pointInPolygon(mx + e.nx * PROBE, my + e.ny * PROBE, poly));
        if (keep && runStart == null) runStart = t0;
        if (!keep && runStart != null) {
          out.push([at(runStart), at(t0)]);
          runStart = null;
        }
      }
      if (runStart != null) out.push([at(runStart), at(1)]);
    }),
  );
  return out;
}

/**
 * One SVG path for the fill of a merged room: every polygon and bridge as a subpath,
 * all wound the same way, so with ``fill-rule="nonzero"`` overlaps fill once (no
 * darker double-alpha where parts overlap).
 */
export function mergedFillPath(polygons: number[][][], bridges: number[][][] = []): string {
  return [...polygons, ...bridges]
    .filter((poly) => poly.length >= 3)
    .map((poly) => (signedArea(poly) < 0 ? [...poly].reverse() : poly))
    .map((poly) => `M${poly.map((p) => `${p[0]},${p[1]}`).join("L")}Z`)
    .join("");
}

/** Area-weighted centroid of all polygons of a merged room — where its one label goes. */
export function mergedLabelPoint(polygons: number[][][]): [number, number] {
  let total = 0;
  let sx = 0;
  let sy = 0;
  for (const poly of polygons) {
    let a = 0;
    let cx = 0;
    let cy = 0;
    poly.forEach((p, i) => {
      const q = poly[(i + 1) % poly.length];
      const cross = p[0] * q[1] - q[0] * p[1];
      a += cross;
      cx += (p[0] + q[0]) * cross;
      cy += (p[1] + q[1]) * cross;
    });
    if (Math.abs(a) < 1e-10) continue;
    // cx/(3a) is the centroid; weight it by |a|/2.
    const w = Math.abs(a) / 2;
    total += w;
    sx += (cx / (3 * a)) * w;
    sy += (cy / (3 * a)) * w;
  }
  if (total === 0) {
    const pts = polygons.flat();
    if (pts.length === 0) return [0, 0];
    return [pts.reduce((s, p) => s + p[0], 0) / pts.length, pts.reduce((s, p) => s + p[1], 0) / pts.length];
  }
  return [sx / total, sy / total];
}

/**
 * The room id to select for ``id``: a merged part's id maps to its main room
 * (when the main room is in ``rooms``), anything else is returned unchanged.
 */
export function resolveMergedRoomId<T extends number | null | undefined>(rooms: FloorplanRoom[], id: T): T | number {
  if (id == null) return id;
  const part = rooms.find((r) => r.id === id);
  if (part?.mergedIntoId != null && rooms.some((r) => r.id === part.mergedIntoId)) return part.mergedIntoId;
  return id;
}

/** The points a room zooms to: every polygon of a merged room, otherwise the first one (as before). */
export function roomZoomPoints(room: FloorplanRoom): number[][] {
  return room.mergedPartIds?.length ? room.polygon.flat() : (room.polygon?.[0] ?? []);
}
